import { useState } from 'react';
import { CheckCircle2, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { QuickStatOverrideDialog } from '@/components/live-scoring/QuickStatOverrideDialog';
import type {
  LeaderScoringCandidate,
  LeaderScoringQuestionProps,
} from '@/utils/scoring/adapters/leaderScoringAdapter';

/**
 * LeaderScoringQuestion (DKPLAT-354, manual overrides DKPLAT-442)
 *
 * Live-scoring template for `player_leader` / `team_leader` questions. These
 * are cross-fixture, so there's no single match feed to overlay — instead we
 * render the candidate list ranked by their accumulated stat value (live once
 * games run, season-to-date pre-game), with the winning option highlighted.
 *
 * When manual scoring is enabled the admin can override a candidate's stat
 * value via the standard QuickStatOverrideDialog (per-candidate pencil). Leader
 * questions have no single fixture, so we pass the "LEADER" sentinel as the
 * dialog's fixture prop (display-only) — the backend recognises it and persists
 * the override against the leaderboard stat.
 */
interface LeaderScoringQuestionComponentProps extends LeaderScoringQuestionProps {
  questionId?: string;
  manualScoring?: boolean;
  onOverrideSuccess?: () => void;
}

export function LeaderScoringQuestion({
  kind,
  statLabel,
  options,
  questionId,
  manualScoring = false,
  onOverrideSuccess,
}: LeaderScoringQuestionComponentProps) {
  if (options.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-4 text-center">
        No candidates configured for this leader question.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header row — labels the ranked stat column */}
      <div className="flex items-center gap-3 px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span className="w-6 flex-shrink-0 text-center">#</span>
        <span className="flex-1">{kind === 'team' ? 'Team' : 'Player'}</span>
        <span className="flex-shrink-0 text-right">{statLabel}</span>
      </div>

      <div className="flex flex-col gap-2">
        {options.map((option, index) => (
          <LeaderCandidateRow
            key={option.option_id || index}
            kind={kind}
            option={option}
            rank={index + 1}
            questionId={questionId}
            manualScoring={manualScoring}
            onOverrideSuccess={onOverrideSuccess}
          />
        ))}
      </div>
    </div>
  );
}

interface LeaderCandidateRowProps {
  kind: 'player' | 'team';
  option: LeaderScoringCandidate;
  rank: number;
  questionId?: string;
  manualScoring: boolean;
  onOverrideSuccess?: () => void;
}

function LeaderCandidateRow({
  kind,
  option,
  rank,
  questionId,
  manualScoring,
  onOverrideSuccess,
}: LeaderCandidateRowProps) {
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);
  const image = option.imageUrl || option.teamLogoUrl;
  const entityId = kind === 'team' ? option.teamId : option.playerId;

  // The pencil only makes sense when we have everything the dialog needs:
  // manual scoring on, a question id, the entity id and the stat to override.
  const canOverride = Boolean(manualScoring && questionId && entityId && option.statType);

  return (
    <div
      className={cn(
        'flex items-center gap-3 w-full p-3 rounded-lg border transition-colors',
        option.is_correct ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
      )}
    >
      {/* Rank — replaced by a check on the winning candidate */}
      <div className="w-6 flex-shrink-0 flex items-center justify-center">
        {option.is_correct ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : (
          <span className="text-sm font-semibold text-gray-400">{rank}</span>
        )}
      </div>

      {/* Candidate image (player headshot / team logo) */}
      {image && (
        <img
          src={image}
          alt={option.name}
          className={cn(
            'h-10 w-10 object-contain flex-shrink-0',
            kind === 'player' ? 'rounded-full' : 'rounded'
          )}
        />
      )}

      {/* Name + meta line */}
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'text-sm font-medium truncate',
            option.is_correct ? 'text-gray-900' : 'text-gray-700'
          )}
        >
          {option.name}
        </div>
        {(option.position || option.teamShortcode || option.fixtureLabel) && (
          <div className="text-xs text-muted-foreground truncate">
            {[option.position, option.teamShortcode, option.fixtureLabel]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
      </div>

      {/* Stat value + edit control */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <span
          className={cn(
            'text-lg font-semibold tabular-nums',
            option.is_correct ? 'text-green-600' : 'text-gray-600'
          )}
        >
          {option.statValue.toLocaleString()}
        </span>
        {canOverride && (
          <button
            type="button"
            onClick={() => setIsOverrideDialogOpen(true)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Edit stat value"
          >
            <Pencil className="h-4 w-4 text-blue-400" />
          </button>
        )}
      </div>

      {/* Quick Stat Override Dialog — leader questions have no single fixture, so
          we pass the "LEADER" sentinel for display context only. */}
      {canOverride && questionId && (
        <QuickStatOverrideDialog
          open={isOverrideDialogOpen}
          onOpenChange={setIsOverrideDialogOpen}
          questionId={questionId}
          fixtureId="LEADER"
          playerId={kind === 'player' ? option.playerId : undefined}
          teamId={kind === 'team' ? option.teamId : undefined}
          statType={option.statType}
          currentValue={option.statValue}
          onSuccess={() => {
            setIsOverrideDialogOpen(false);
            onOverrideSuccess?.();
          }}
        />
      )}
    </div>
  );
}
